/*
 * KB Product Details
 * Gallery (arrows, thumbnails, swipe, keys, autoplay), variants, stock status,
 * delivery estimate with free delivery progress, Klarna visibility, sticky add to cart.
 */
(function () {
  'use strict';

  var DAY = 86400000;

  /* ---------- helpers ---------- */

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function toInt(value, min, max, fallback) {
    var n = parseInt(value, 10);
    if (isNaN(n)) return fallback;
    return Math.min(max, Math.max(min, n));
  }

  // Runs fn once every deferred theme script (pubsub.js, constants.js...) has executed.
  function afterScripts(fn) {
    var done = false;
    function run() { if (done) return; done = true; fn(); }
    if (document.readyState === 'complete') { run(); return; }
    document.addEventListener('DOMContentLoaded', run);
    window.addEventListener('load', run);
  }

  function makeMoney(currency) {
    var formatter = null;
    try {
      formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: currency, minimumFractionDigits: 2
      });
    } catch (e) {}
    return function (cents) {
      var value = (Number(cents) || 0) / 100;
      return formatter ? formatter.format(value) : value.toFixed(2);
    };
  }

  /* ---------- gallery ---------- */

  function initGallery(root) {
    var noop = { showMedia: function () {}, destroy: function () {} };
    var gallery = qs('[data-kb-gallery]', root);
    if (!gallery) return noop;

    var img = qs('.kb-product-gallery__main-image', gallery);
    var main = qs('[data-kb-gallery-main]', gallery);
    var strip = qs('[data-kb-thumbs]', gallery);
    var thumbs = qsa('[data-kb-thumb]', gallery);
    var prevBtn = qs('[data-kb-main-prev]', gallery);
    var nextBtn = qs('[data-kb-main-next]', gallery);
    var counter = qs('[data-kb-counter-current]', gallery);
    var included = qs('[data-kb-included]', gallery);
    if (!thumbs.length) return noop;

    var index = thumbs.findIndex(function (t) { return t.classList.contains('is-active'); });
    if (index < 0) index = 0;

    var autoplay = gallery.dataset.autoplay === 'true' && thumbs.length > 1 && !reducedMotion();
    var delay = toInt(gallery.dataset.autoplaySeconds, 2, 30, 4) * 1000;
    var timer = null;
    var stopped = false; // set once the shopper takes control
    var hovering = false;
    var focused = false;
    var inView = true;
    var observer = null;

    function preload(i) {
      var t = thumbs[(i + thumbs.length) % thumbs.length];
      if (!t || t.dataset.kbPreloaded) return;
      t.dataset.kbPreloaded = 'true';
      var p = new Image();
      if (img && img.getAttribute('sizes')) p.sizes = img.getAttribute('sizes');
      if (t.dataset.imageSrcset) p.srcset = t.dataset.imageSrcset;
      p.src = t.dataset.imageSrc;
    }

    // Scrolls only the thumbnail strip (never the page) so the active thumb is centred.
    function centerThumb(thumb, smooth) {
      if (!strip || !thumb || strip.scrollWidth <= strip.clientWidth) return;
      var target = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
      target = Math.max(0, Math.min(strip.scrollWidth - strip.clientWidth, target));
      if (typeof strip.scrollTo === 'function') {
        strip.scrollTo({ left: target, behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
      } else {
        strip.scrollLeft = target;
      }
    }

    function swapImage(thumb) {
      if (!img || !thumb.dataset.imageSrc) return;
      var finish = function () { img.classList.remove('is-swapping'); };
      img.classList.add('is-swapping');
      img.addEventListener('load', finish, { once: true });
      img.addEventListener('error', finish, { once: true });
      if (thumb.dataset.imageSrcset) img.srcset = thumb.dataset.imageSrcset;
      else img.removeAttribute('srcset');
      img.src = thumb.dataset.imageSrc;
      img.alt = thumb.dataset.imageAlt || '';
      if (img.complete && img.naturalWidth) finish();
    }

    function show(i, opts) {
      opts = opts || {};
      var count = thumbs.length;
      i = ((i % count) + count) % count;
      var changed = i !== index;
      index = i;

      thumbs.forEach(function (t, n) {
        var active = n === i;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (changed) swapImage(thumbs[i]);
      if (counter) counter.textContent = String(i + 1);
      if (included) included.hidden = i !== 0; /* the badge belongs to the first image only */
      centerThumb(thumbs[i], opts.smooth !== false);
      preload(i + 1);
      preload(i - 1);
    }

    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
    }

    function start() {
      stop();
      if (!autoplay || stopped || hovering || focused || !inView || document.hidden) return;
      timer = setInterval(function () { show(index + 1); }, delay);
    }

    function takeControl() {
      stopped = true;
      stop();
    }

    thumbs.forEach(function (thumb, n) {
      thumb.addEventListener('click', function () { takeControl(); show(n); });
    });
    if (prevBtn) prevBtn.addEventListener('click', function () { takeControl(); show(index - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { takeControl(); show(index + 1); });

    gallery.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      takeControl();
      e.preventDefault();
      show(index + (e.key === 'ArrowRight' ? 1 : -1));
      var active = document.activeElement;
      if (active && active.hasAttribute('data-kb-thumb')) thumbs[index].focus({ preventScroll: true });
    });

    // Swipe on the main image (mobile)
    var touchX = null;
    var touchY = null;
    if (main) {
      main.addEventListener('touchstart', function (e) {
        var t = e.touches[0];
        touchX = t.clientX;
        touchY = t.clientY;
      }, { passive: true });
      main.addEventListener('touchend', function (e) {
        if (touchX === null) return;
        var t = e.changedTouches[0];
        var dx = t.clientX - touchX;
        var dy = t.clientY - touchY;
        touchX = touchY = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
        takeControl();
        show(index + (dx < 0 ? 1 : -1));
      }, { passive: true });
    }

    gallery.addEventListener('mouseenter', function () { hovering = true; stop(); });
    gallery.addEventListener('mouseleave', function () { hovering = false; start(); });
    gallery.addEventListener('focusin', function () { focused = true; stop(); });
    gallery.addEventListener('focusout', function () { focused = false; start(); });

    function onVisibility() { if (document.hidden) stop(); else start(); }
    document.addEventListener('visibilitychange', onVisibility);

    if (autoplay && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        if (inView) start(); else stop();
      }, { threshold: 0.35 });
      observer.observe(gallery);
    }

    show(index, { smooth: false });
    start();

    return {
      showMedia: function (mediaId) {
        if (!mediaId) return;
        var n = thumbs.findIndex(function (t) { return t.dataset.mediaId === String(mediaId); });
        if (n < 0) return;
        takeControl();
        show(n);
      },
      destroy: function () {
        stop();
        document.removeEventListener('visibilitychange', onVisibility);
        if (observer) observer.disconnect();
      }
    };
  }

  /* ---------- stock status ---------- */

  function initStock(root) {
    var el = qs('[data-kb-stock]', root);
    if (!el) return null;
    var text = qs('[data-kb-stock-text]', el);
    var threshold = toInt(el.dataset.threshold, 0, 1000, 0);

    return {
      update: function (variant) {
        if (!variant) { el.hidden = true; return; }
        var tracked = variant.inventory_management === 'shopify';
        var qty = Number(variant.inventory_quantity) || 0;
        var state = 'in';
        var msg = el.dataset.textIn || '';

        if (!variant.available) {
          state = 'out';
          msg = el.dataset.textOut || '';
        } else if (tracked && qty <= 0) {
          state = 'backorder';
          msg = el.dataset.textBackorder || '';
        } else if (tracked && threshold > 0 && qty <= threshold) {
          state = 'low';
          msg = (el.dataset.textLow || '').replace('[count]', String(qty));
        }

        el.className = 'kb-stock kb-stock--' + state;
        if (text) text.textContent = msg;
        el.hidden = !msg;
      }
    };
  }

  /* ---------- Klarna (visibility only; copy is Klarna-approved and fixed) ---------- */

  function initKlarna(root) {
    var el = qs('[data-kb-klarna]', root);
    if (!el) return null;
    var min = Number(el.dataset.minCents) || 0;
    return {
      update: function (variant, qty) {
        var ok = !!(variant && variant.available) && (min <= 0 || variant.price * qty >= min);
        el.hidden = !ok;
      }
    };
  }

  /* ---------- delivery estimate + free delivery progress ---------- */

  function londonNow() {
    var parts = {};
    try {
      new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London', year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', hourCycle: 'h23'
      }).formatToParts(new Date()).forEach(function (p) { parts[p.type] = p.value; });
    } catch (e) {
      var d = new Date();
      parts = { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(), hour: d.getHours(), minute: d.getMinutes() };
    }
    return {
      date: Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)),
      minutes: (Number(parts.hour) % 24) * 60 + Number(parts.minute)
    };
  }

  function initDelivery(root, money) {
    var el = qs('[data-kb-delivery]', root);
    if (!el) return null;

    var timingEl = qs('[data-kb-delivery-timing]', el);
    var dispatchEl = qs('[data-kb-dispatch]', el);
    var etaEl = qs('[data-kb-eta]', el);
    var freeEl = qs('[data-kb-free]', el);
    var freeText = qs('[data-kb-free-text]', el);
    var freeBar = qs('[data-kb-free-bar]', el);

    // Business rule (fixed in code, not read from the section settings):
    // orders placed before 12pm UK time count as that working day, and dispatch is
    // within 72 hours, i.e. 3 working days from the day the order is counted.
    var CUTOFF_HOUR = 12;
    var DISPATCH_WORKING_DAYS = 3;

    var cutoff = CUTOFF_HOUR * 60;
    var dispatchDays = DISPATCH_WORKING_DAYS;
    var minDays = toInt(el.dataset.min, 1, 60, 2);
    var maxDays = Math.max(minDays, toInt(el.dataset.max, 1, 60, 4));
    var saturday = el.dataset.saturday === 'true';
    var closed = {};
    (el.dataset.closed || '').split(/[^0-9-]+/).forEach(function (d) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) closed[d] = true;
    });

    var rate = 1;
    try { rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1; } catch (e) {}
    var threshold = Math.round((Number(el.dataset.freeThreshold) || 0) * rate);

    var cartTotal = null;
    var lineTotal = 0;
    var canShip = false;
    var backorder = false;
    var unsubscribe = null;

    // Fixed English formats so output is identical in every browser ("Thu 24 Sep").
    var WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var WD_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var fmtDay = { format: function (t) { var d = new Date(t); return WD[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + MO[d.getUTCMonth()]; } };
    var fmtDayShort = { format: function (t) { var d = new Date(t); return WD[d.getUTCDay()] + ' ' + d.getUTCDate(); } };
    var fmtWeekday = { format: function (t) { return WD_LONG[new Date(t).getUTCDay()]; } };

    function iso(t) { return new Date(t).toISOString().slice(0, 10); }
    function weekday(t) { return new Date(t).getUTCDay(); }
    function isDispatchDay(t) {
      var w = weekday(t);
      return !closed[iso(t)] && ((w >= 1 && w <= 5) || (saturday && w === 6));
    }
    function isDeliveryDay(t) {
      var w = weekday(t);
      return !closed[iso(t)] && w >= 1 && w <= 5;
    }
    function next(t, test) {
      var n = t;
      var guard = 0;
      do { n += DAY; guard += 1; } while (!test(n) && guard < 90);
      return n;
    }
    function advance(t, count, test) {
      for (var i = 0; i < count; i++) t = next(t, test);
      return t;
    }
    function sameMonth(a, b) {
      var x = new Date(a);
      var y = new Date(b);
      return x.getUTCMonth() === y.getUTCMonth() && x.getUTCFullYear() === y.getUTCFullYear();
    }

    // Both lines are fixed wording: no countdown, no computed dispatch day, no delivery dates.
    var DISPATCH_TEXT = 'Order before <strong>12pm</strong>';
    var ETA_TEXT = 'Dispatched <strong>within 72 hours</strong>, delivered within 3 working days of dispatch';

    function renderTiming() {
      if (dispatchEl) dispatchEl.innerHTML = DISPATCH_TEXT;
      if (etaEl) etaEl.innerHTML = ETA_TEXT;
    }

    function renderFree() {
      if (!freeEl || threshold <= 0) return;
      if (cartTotal === null || !canShip) { freeEl.hidden = true; return; }

      var withLine = cartTotal + lineTotal;
      var msg;
      if (cartTotal >= threshold) {
        msg = 'Your basket qualifies for <strong>free delivery</strong>';
      } else if (withLine >= threshold) {
        msg = 'Add this to your basket to get <strong>free delivery</strong>';
      } else if (cartTotal === 0) {
        msg = '<strong>Free delivery</strong> on orders over ' + money(threshold);
      } else {
        msg = 'You\u2019re <strong>' + money(threshold - cartTotal) + '</strong> away from free delivery';
      }

      freeText.innerHTML = msg;
      freeBar.style.setProperty('--kb-cart', Math.min(100, (cartTotal / threshold) * 100) + '%');
      freeBar.style.setProperty('--kb-with', Math.min(100, (withLine / threshold) * 100) + '%');
      freeEl.classList.toggle('is-complete', cartTotal >= threshold);
      freeEl.hidden = false;
    }

    function syncVisibility() {
      var hasFree = !!(freeEl && threshold > 0);
      if (timingEl) timingEl.hidden = backorder;
      el.hidden = !canShip || (backorder && !hasFree);
    }

    function fetchCart() {
      if (!freeEl || threshold <= 0 || !window.fetch) return;
      var base = (window.routes && window.routes.cart_url) || '/cart';
      fetch(base + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          cartTotal = Number(cart.total_price) || 0;
          renderFree();
        })
        .catch(function () {});
    }

    function onVisibility() {
      if (document.hidden) return;
      renderTiming();
      fetchCart();
    }

    renderTiming();
    fetchCart();
    var ticker = setInterval(renderTiming, 30000);
    document.addEventListener('visibilitychange', onVisibility);

    afterScripts(function () {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, fetchCart);
      }
    });

    return {
      update: function (variant, qty) {
        canShip = !!(variant && variant.available);
        backorder = canShip && variant.inventory_management === 'shopify' && Number(variant.inventory_quantity) <= 0;
        lineTotal = canShip ? variant.price * qty : 0;
        syncVisibility();
        renderFree();
      },
      destroy: function () {
        clearInterval(ticker);
        document.removeEventListener('visibilitychange', onVisibility);
        if (typeof unsubscribe === 'function') unsubscribe();
      }
    };
  }

  /* ---------- sticky add to cart ---------- */

  function initSticky(root, money) {
    var bar = qs('[data-kb-sticky]', root);
    var anchor = qs('.kb-product-actions', root);
    if (!bar) return null;
    if (!anchor) { bar.remove(); return null; }

    var button = qs('[data-kb-sticky-add]', bar);
    var label = qs('[data-kb-sticky-label]', bar);
    var price = qs('[data-kb-sticky-price]', bar);

    // Move to <body> so theme animations (transform on the section) can't break position: fixed.
    document.body.appendChild(bar);

    // Scroll check (rAF-throttled) rather than IntersectionObserver alone: a fast jump
    // from below the fold to past the button never "intersects", so IO would miss it.
    var ticking = false;
    function check() {
      ticking = false;
      var visible = anchor.getBoundingClientRect().bottom < 0;
      if (visible === bar.classList.contains('is-visible')) return;
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(check);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    check();

    return {
      update: function (variant, addText) {
        if (price && variant) price.textContent = money(variant.price);
        if (button) button.disabled = !(variant && variant.available);
        if (label) label.textContent = !variant ? 'Unavailable' : (variant.available ? addText : 'Sold Out');
      },
      destroy: function () {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        bar.remove();
      }
    };
  }

  /* ---------- main ---------- */

  /* ---------------------------------------------------------------------
   * Tabs / accordion
   * ------------------------------------------------------------------- */
  function initTabs(root) {
    var wrap = qs('[data-kb-tabs]', root);
    if (!wrap) return null;

    var tablist = qs('[data-kb-tablist]', wrap);
    var tabs = qsa('[role="tab"][data-kb-tab]', wrap);
    var triggers = qsa('[data-kb-acc]', wrap);
    var panels = qsa('[data-kb-panel]', wrap);
    var mq = window.matchMedia('(max-width: 749px)');
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var hashMap = {
      reviews: 'review', review: 'review', judgeme_product_reviews: 'review',
      specification: 'specification', spec: 'specification',
      description: 'description', installation: 'installation', additional: 'additional'
    };

    function has(name) { return panels.some(function (p) { return p.dataset.kbPanel === name; }); }
    function current() {
      var open = panels.filter(function (p) { return !p.hidden; })[0];
      return open ? open.dataset.kbPanel : null;
    }

    function show(name, focusTab) {
      tabs.forEach(function (t) {
        var on = t.dataset.kbTab === name;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (on && focusTab) t.focus();
      });
      triggers.forEach(function (b) { b.setAttribute('aria-expanded', b.dataset.kbAcc === name ? 'true' : 'false'); });
      panels.forEach(function (p) {
        var on = p.dataset.kbPanel === name;
        p.classList.toggle('is-active', on);
        p.hidden = !on;
      });
    }

    function scrollToTabs(name) {
      var target = mq.matches ? qs('[data-kb-acc="' + name + '"]', wrap) : wrap;
      if (!target) return;
      requestAnimationFrame(function () {
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      });
    }

    function open(name, scroll) {
      if (!has(name)) return false;
      show(name);
      if (scroll) scrollToTabs(name);
      return true;
    }

    tabs.forEach(function (t) {
      t.addEventListener('click', function () { show(t.dataset.kbTab); });
    });

    if (tablist) {
      tablist.addEventListener('keydown', function (e) {
        var i = tabs.indexOf(document.activeElement);
        if (i < 0) return;
        var next = null;
        if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        e.preventDefault();
        show(tabs[next].dataset.kbTab, true);
      });
    }

    triggers.forEach(function (b) {
      b.addEventListener('click', function () {
        var name = b.dataset.kbAcc;
        if (current() === name) {
          show(null);
          return;
        }
        show(name);
        // Closing a long panel above can push this trigger off-screen; bring it back.
        requestAnimationFrame(function () {
          if (b.getBoundingClientRect().top < 0) b.scrollIntoView({ block: 'start' });
        });
      });
    });

    // Desktop tabs always need one panel open.
    function onBreakpoint() {
      if (!mq.matches && !current() && tabs[0]) show(tabs[0].dataset.kbTab);
    }
    if (mq.addEventListener) mq.addEventListener('change', onBreakpoint);
    else if (mq.addListener) mq.addListener(onBreakpoint);

    // Star rating in the info column opens the Reviews tab (capture phase so it runs before the app's own scroll).
    function onRatingClick(e) {
      var hit = e.target.closest && e.target.closest('.jdgm-preview-badge, [data-kb-open-reviews]');
      if (hit && root.contains(hit)) open('review', true);
    }
    root.addEventListener('click', onRatingClick, true);

    // Deep links: #reviews, #installation, #judgeme_product_reviews ...
    function onHash() {
      var key = (window.location.hash || '').replace('#', '').toLowerCase();
      if (hashMap[key]) open(hashMap[key], true);
    }
    window.addEventListener('hashchange', onHash);
    onHash();

    return {
      open: open,
      destroy: function () {
        if (mq.removeEventListener) mq.removeEventListener('change', onBreakpoint);
        else if (mq.removeListener) mq.removeListener(onBreakpoint);
        root.removeEventListener('click', onRatingClick, true);
        window.removeEventListener('hashchange', onHash);
      }
    };
  }

  function init(root) {
    if (!root || root.dataset.kbReady === 'true') return;
    root.dataset.kbReady = 'true';

    var variants = [];
    var variantScript = qs('[data-kb-variants]', root);
    try { variants = JSON.parse(variantScript ? variantScript.textContent : '[]') || []; } catch (e) { variants = []; }

    var money = makeMoney(root.dataset.currency || 'GBP');
    var hiddenId = qs('[data-kb-variant-id]', root);
    var price = qs('[data-kb-price]', root);
    var compare = qs('[data-kb-compare]', root);
    var discount = qs('[data-kb-discount]', root);
    var sku = qs('[data-kb-sku]', root);
    var skuWrap = qs('[data-kb-sku-wrap]', root);
    var productMeta = qs('[data-kb-product-meta]', root);
    var add = qs('[data-kb-add]', root);
    var addLabel = qs('[data-kb-add-label]', root);
    var addText = (add && add.dataset.addLabel) || 'Add to Cart';
    var qty = qs('[data-kb-qty-input]', root);

    var gallery = initGallery(root);
    var stock = initStock(root);
    var klarna = initKlarna(root);
    var delivery = initDelivery(root, money);
    var sticky = initSticky(root, money);

    var current = null;
    if (hiddenId) {
      current = variants.find(function (v) { return String(v.id) === String(hiddenId.value); }) || null;
    }
    if (!current && variants.length === 1) current = variants[0];

    function quantity() {
      return Math.max(1, parseInt(qty && qty.value, 10) || 1);
    }

    function syncModules() {
      var q = quantity();
      if (stock) stock.update(current);
      if (klarna) klarna.update(current, q);
      if (delivery) delivery.update(current, q);
      if (sticky) sticky.update(current, addText);
    }

    function selectedOptions() {
      return qsa('[data-kb-option]', root).map(function (wrap) {
        var select = qs('[data-kb-option-select]', wrap);
        if (select) return select.value;
        var selected = qs('[data-kb-option-value].is-selected', wrap);
        return selected ? selected.dataset.value : '';
      });
    }

    function updateVariant() {
      var options = selectedOptions();
      var variant = variants.find(function (item) {
        return item.options && item.options.length === options.length && item.options.every(function (value, i) {
          return String(value) === String(options[i]);
        });
      });

      current = variant || null;

      if (!variant) {
        if (add) add.disabled = true;
        if (addLabel) addLabel.textContent = 'Unavailable';
        syncModules();
        return;
      }

      if (hiddenId) {
        hiddenId.value = variant.id;
        hiddenId.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (price) price.textContent = money(variant.price);

      if (sku) {
        var hasSku = !!(variant.sku && String(variant.sku).trim());
        sku.textContent = hasSku ? variant.sku : '';
        if (skuWrap) skuWrap.hidden = !hasSku;
        if (productMeta) productMeta.hidden = !hasSku && productMeta.dataset.hasCategory !== 'true';
      }

      var hasCompare = Number(variant.compare_at_price) > Number(variant.price);
      if (compare) {
        compare.classList.toggle('is-hidden', !hasCompare);
        compare.textContent = hasCompare ? money(variant.compare_at_price) : '';
      }
      if (discount) {
        if (hasCompare) {
          var pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          discount.textContent = pct + '% Off';
          discount.classList.remove('is-hidden');
        } else {
          discount.textContent = '';
          discount.classList.add('is-hidden');
        }
      }

      if (add) add.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? addText : 'Sold Out';

      gallery.showMedia(variant.featured_media_id);
      syncModules();

      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {}
    }

    qsa('[data-kb-option-select]', root).forEach(function (select) {
      select.addEventListener('change', updateVariant);
    });

    qsa('[data-kb-option-value]', root).forEach(function (button) {
      button.addEventListener('click', function () {
        var wrap = button.closest('[data-kb-option]');
        qsa('[data-kb-option-value]', wrap).forEach(function (item) {
          var active = item === button;
          item.classList.toggle('is-selected', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        updateVariant();
      });
    });

    // Quantity
    var minus = qs('[data-kb-qty-minus]', root);
    var plus = qs('[data-kb-qty-plus]', root);
    function setQty(value) {
      if (!qty) return;
      qty.value = Math.max(1, value);
      syncModules();
    }
    if (minus) minus.addEventListener('click', function () { setQty(quantity() - 1); });
    if (plus) plus.addEventListener('click', function () { setQty(quantity() + 1); });
    if (qty) {
      qty.addEventListener('input', syncModules);
      qty.addEventListener('change', function () { qty.value = quantity(); syncModules(); });
    }

    // Tabs (desktop) and accordion (mobile) share one open panel
    var tabs = initTabs(root);

    syncModules();

    root._kbDestroy = function () {
      gallery.destroy();
      if (delivery) delivery.destroy();
      if (sticky) sticky.destroy();
      if (tabs) tabs.destroy();
      root.dataset.kbReady = '';
    };
  }

  function boot(scope) { qsa('[data-kb-product]', scope).forEach(init); }

  boot(document);
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    qsa('[data-kb-product]', e.target).forEach(function (r) {
      if (typeof r._kbDestroy === 'function') r._kbDestroy();
    });
  });
})();